import { PreAuthRouteDecision } from '@rapid-recovery-agency-inc/insightt-graphql';

import { getAppAuthBoundary } from './auth-boundary';
import { PROVIDER_LOGOUT_TIMEOUT_MS, PUSH_DEVICE_RELEASE_TIMEOUT_MS, withTimeout } from './bounded';
// NOTE: explicit `.ts` extension — see constants.ts for why (`types.js` collision).
import { CleanupTransactionStatus } from './types.ts';

/**
 * The one sign-out owner for every trigger (T065, T066, T067, T087; FR-021, FR-022; US5).
 *
 * Explicit logout (the logout saga) and every unauthorized trigger (Apollo `ACCESS_DENIED` and
 * `SESSION_TIMEOUT`, apisauce 401s and expired tokens, the subscription client) call
 * {@link signOut}. Per page it runs once:
 *
 * 1. clears all local state first — storage, cookies, filters, company/branch selection, domain
 *    Redux slices, the Apollo cache, legacy caches, Intercom and analytics identity — then the auth
 *    boundary's credentials (`system/utils.ts`'s `cleanSessionData`). Just before, in either mode,
 *    it releases this browser's push device with the current token, because an SSO provider logout
 *    leaves the page during cleanup;
 * 2. for an SSO session, the boundary attempts the client-scoped `end_session`, which navigates to
 *    the provider and returns to {@link SIGNED_OUT_PATH};
 * 3. for a legacy session, attempts the legacy server logout with the token captured before step 1;
 * 4. navigates exactly once, to the caller's destination or the sign-in page. A legacy termination
 *    that could not be confirmed is reported as `partial_logout` but shown no differently (decided
 *    2026-10-01: no separate logged-out screen). An SSO sign-out reports its one terminal event when
 *    the provider attempt starts, so a provider failure after that is not reported again (approved
 *    exception, spec clarification 2026-10-06).
 *
 * `system/utils.ts` is imported lazily: a static import would close the
 * `shared/utils → shared/analytics → redux/store → subscription-api → shared/utils` cycle that made
 * T037 revert the same cleanup when it was added to `shared/utils.ts` directly.
 */

/** Where every sign-out ends unless the caller names a destination: the sign-in page. */
export const SIGNED_OUT_PATH = '/user/login';

export interface SignOutOptions {
  /** What started the sign-out; reported as the `logout` or `unauthorized` stage (FR-025). */
  readonly trigger?: 'explicit' | 'unauthorized';
  /** Where to go after sign-out instead of the sign-in page. */
  readonly destination?: string;
  /**
   * Ends the legacy server session with the token captured before local clearing. Resolves `true`
   * when the server confirmed it, `false` otherwise.
   */
  readonly terminateLegacyServerSession?: (accessToken: string) => Promise<boolean>;
  /**
   * Stops push notifications to this browser, with the token captured before local clearing. Best
   * effort: a failure or timeout never blocks the sign-out.
   */
  readonly releaseDeviceToken?: (accessToken: string) => Promise<unknown>;
}

let signOutInFlight: Promise<void> | null = null;

/** Starts, or joins, this page's single sign-out. Never rejects. */
export function signOut(options: SignOutOptions = {}): Promise<void> {
  if (!signOutInFlight) {
    // A failure part-way through still ends signed out, on the caller's destination or the sign-in page.
    signOutInFlight = runSignOut(options).catch(() => {
      // Navigation is best-effort: when the environment is already gone (for example, a test's
      // jsdom teardown), there is no `window` to navigate. Swallowing that here keeps the
      // "never rejects" contract and avoids an unhandled rejection escaping after teardown.
      if (typeof window !== 'undefined') {
        window.location.replace(options.destination ?? SIGNED_OUT_PATH);
      }
    });
  }
  return signOutInFlight;
}

const confirmLegacyTermination = (
  terminate: (accessToken: string) => Promise<boolean>,
  accessToken: string,
): Promise<boolean> =>
  withTimeout(terminate(accessToken), PROVIDER_LOGOUT_TIMEOUT_MS, 'Legacy logout').then(
    (confirmed) => confirmed,
    () => false,
  );

async function runSignOut(options: SignOutOptions): Promise<void> {
  const boundary = getAppAuthBoundary();
  // Captured before local clearing, which removes it, so the legacy server logout can still run.
  const accessToken = boundary.getAccessToken();
  const legacyToken = boundary.getAuthMode() === PreAuthRouteDecision.LEGACY ? accessToken : null;

  // Loaded lazily to avoid the `shared/utils → redux/store` cycle, and before cleanup so the event
  // can be sent synchronously from `onProviderLogoutStart`.
  const [{ cleanSessionData }, { reportAuthenticationOutcome }] = await Promise.all([
    import('../system/utils'),
    import('./events'),
  ]);
  const explicit = options.trigger === 'explicit';
  const stage = explicit ? 'logout' : 'unauthorized';
  const interactionType = explicit ? 'user' : 'automatic';
  let reported = false;

  if (accessToken && options.releaseDeviceToken) {
    await withTimeout(
      options.releaseDeviceToken(accessToken),
      PUSH_DEVICE_RELEASE_TIMEOUT_MS,
      'Push device release',
    ).catch(() => undefined);
  }

  await cleanSessionData({
    postLogoutPath: options.destination ?? SIGNED_OUT_PATH,
    // An SSO provider logout leaves the page before cleanup settles, so the terminal event goes out
    // as a beacon first (T104). If the provider then fails within its bound, the partial result is
    // not reported, because this one event has already reported success.
    onProviderLogoutStart: () => {
      reported = true;
      reportAuthenticationOutcome({ stage, outcome: 'succeeded', reason: 'none', interactionType });
    },
  });

  const legacyConfirmed =
    legacyToken && options.terminateLegacyServerSession
      ? await confirmLegacyTermination(options.terminateLegacyServerSession, legacyToken)
      : true;
  const partial = !legacyConfirmed || boundary.getLastCleanupStatus() === CleanupTransactionStatus.PARTIAL_LOGOUT;

  if (!reported) {
    // Queued as a beacon before the navigation below.
    reportAuthenticationOutcome({
      stage,
      outcome: partial ? 'failed' : 'succeeded',
      reason: partial ? 'partial_logout' : 'none',
      interactionType,
    });
  }

  window.location.replace(options.destination ?? SIGNED_OUT_PATH);
}

/** Test-only reset for the per-page single-flight guard. */
export function resetSignOutForTesting(): void {
  signOutInFlight = null;
}
